import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/DefaultSelectDropdown";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { NamingConvention, ExportTemplate } from "@/types/exportTemplates";
import { getCachedTemplates } from "@/lib/stores/templatesStore";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { isDatabaseExport, isFolderBasedExport } from "./utils";
import { Cloud } from "lucide-react";

interface DataPackageExportSectionProps {
  gameId: string;
  activeTemplate?: ExportTemplate;
  dataPackageMode: 'all' | 'blueprint' | 'selection';
  selectedBlueprintId: string;
  namingConvention: NamingConvention;
  isExporting: boolean;
  isExportingPackage: boolean;
  isSavingToCloud: boolean;
  onModeChange: (mode: 'all' | 'blueprint' | 'selection') => void;
  onBlueprintChange: (blueprintId: string) => void;
  onNamingConventionChange: (convention: NamingConvention) => void;
  onExport: () => void;
  onSaveToCloud: () => void;
  onNavigateToSettings: () => void;
}

export function DataPackageExportSection({
  gameId,
  activeTemplate,
  dataPackageMode,
  selectedBlueprintId,
  namingConvention,
  isExporting,
  isExportingPackage,
  isSavingToCloud,
  onModeChange,
  onBlueprintChange,
  onNamingConventionChange,
  onExport,
  onSaveToCloud,
  onNavigateToSettings
}: DataPackageExportSectionProps) {
  const { t } = usePlatformTranslation();
  const isDatabaseType = isDatabaseExport(activeTemplate);
  const isFolderBased = isFolderBasedExport(activeTemplate);

  const getDescription = () => {
    if (activeTemplate?.platform === 'json') {
      if (isFolderBased) {
        return `Export all data as structured JSON files organized by folder hierarchy. Ideal for websites, custom engines, or any application that reads JSON.`;
      }
      return `Export all data as structured JSON files organized by category. Produces database, dialogs, translations, types, tags, and variables as separate JSON files.`;
    }
    if (isFolderBased) {
      return `Export all library objects organized by folder structure for strongly-typed dot notation access (e.g., Database.scenes.player_house.bed.inspect_text).`;
    }
    if (isDatabaseType) {
      return `Export all library objects as a single database file organized by category in ${activeTemplate?.platform === 'unity' ? 'C#' : 'Lua'} format.`;
    }
    return `Export glossary items with their templates as ready-to-use code packages. Generates base classes (regeneratable) and user class templates (your custom code).`;
  };

  const getButtonLabel = () => {
    if (isDatabaseType) {
      return 'Download Database File';
    }
    return 'Download Data Package';
  };

  const getInfoDescription = () => {
    if (activeTemplate?.platform === 'json') {
      return 'Export includes: database.json (objects), dialogs.json, translations.json, types.json, tags.json, variables.json, README';
    }
    if (isFolderBased) {
      const className = activeTemplate?.databaseSettings?.className || 'Database';
      const ext = activeTemplate?.platform === 'unity' ? '.cs' : activeTemplate?.platform === 'json' ? '.json' : '.lua';
      return `Export includes: Single ${className}${ext} file with folder-based hierarchy, README with integration instructions`;
    }
    if (isDatabaseType) {
      return `Database export includes: Single ${activeTemplate?.instanceFile.filename} file with all objects organized by category, README with integration instructions`;
    }
    return 'Package includes: Base classes (regeneratable), User templates (customize these), JSON data files, Manifest, Integration README';
  };

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <IconProvider iconstring="fa-box" size={12} className="text-primary" />
        <div className="flex-1">
          <h2 className="heading-4">Data Package Export</h2>
          <p className="text-muted-foreground mb-4">{getDescription()}</p>
          
          {/* Active Template Info */}
          {activeTemplate && (
            <Alert className="mb-4">
              <IconProvider iconstring="fa-info-circle" size={14} className="text-primary" />
              <AlertDescription className="ml-2">
                Using export template: <strong>{activeTemplate.name}</strong>
                {' '}- {activeTemplate.platform.toUpperCase()}
                {' '}
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigateToSettings();
                  }}
                  className="text-primary hover:underline ml-2"
                >
                  Configure Templates →
                </a>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            {/* Export Mode Selection */}
            <div className="space-y-2">
              <Label className="text-foreground">{t('platform.settings.importexportsection.export_mode')}</Label>
              <RadioGroup
                value={dataPackageMode}
                onValueChange={onModeChange}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="mode-all" />
                  <Label htmlFor="mode-all" className="font-normal cursor-pointer text-foreground">
                    All Objects
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="blueprint" id="mode-blueprint" />
                  <Label htmlFor="mode-blueprint" className="font-normal cursor-pointer text-foreground">
                    Items from specific Template
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Blueprint Selection */}
            {dataPackageMode === 'blueprint' && (
              <div className="space-y-2">
                <Label className="text-foreground">{t('platform.settings.importexportsection.select_blueprint')}</Label>
                <Select value={selectedBlueprintId} onValueChange={onBlueprintChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('platform.settings.importexportsection.select_a_blueprint')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getCachedTemplates(gameId).map(blueprint => (
                      <SelectItem key={blueprint.id} value={blueprint.id}>
                        {blueprint.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Naming Convention Selection */}
            <div className="space-y-2">
              <Label className="text-foreground">Naming Convention</Label>
              <Select
                value={namingConvention}
                onValueChange={(value) => onNamingConventionChange(value as NamingConvention)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select naming convention" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Original (As Written)</SelectItem>
                  <SelectItem value="camelCase">camelCase</SelectItem>
                  <SelectItem value="PascalCase">PascalCase</SelectItem>
                  <SelectItem value="snake_case">snake_case</SelectItem>
                  <SelectItem value="SCREAMING_SNAKE_CASE">SCREAMING_SNAKE_CASE</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Controls the casing of folder names, item names, and field names in the export.
              </p>
            </div>
            
            {/* Export Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={onExport}
                disabled={isExportingPackage || isExporting || isSavingToCloud}
                size="lg"
                className="gap-2"
              >
                {isExportingPackage ? (
                  <>
                    <IconProvider iconstring="fa-spinner" className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <IconProvider iconstring="fa-download" size={16} />
                    {getButtonLabel()}
                  </>
                )}
              </Button>
              <Button
                onClick={onSaveToCloud}
                disabled={isExportingPackage || isExporting || isSavingToCloud}
                size="lg"
                variant="outline"
                className="gap-2"
              >
                {isSavingToCloud ? (
                  <>
                    <IconProvider iconstring="fa-spinner" className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Cloud size={16} />
                    Save to Cloud
                  </>
                )}
              </Button>
            </div>
            
            {/* Info Alert */}
            <Alert>
              <IconProvider iconstring="fa-info-circle" className="mr-2" size={16} />
              <AlertDescription>{getInfoDescription()}</AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </div>
  );
}
